import React, { useCallback, useMemo, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { Modal, Button, Typography } from '../../../../../../../components'
import {
  Form,
  FormBody,
  FormGroupContainer
} from '../../../../../../../components/Form/styles'
import Input from '../../../../../../../components/Form/Input'
import Select from '../../../../../../../components/Form/Select'
import ecbrApi from '../../../../../../../services/ecbrApi'
import ErrorMessage from '../../../../../../../components/Form/ErrorMessage'
import { CouponStatus } from '../../../../../../../constants/coupon'
import { isAvailableCoupon } from '../../../../../../../helpers/isAvailableCoupon'
import { dateTypes } from '../../../../../../../constants/date'
import dayjs from '../../../../../../../helpers/dayjs'
import { trimAllStrProps } from '../../../../../../../utils/trimAllStrProps'
import { errorResponseStatusHandler } from '../../../../../../../utils/handlers/errorResponseStatusHandler'

const initialValues = {
  id: '',
  code: '',
  name: '',
  percentage: '',
  quantity: '',
  expiration: dayjs().format(dateTypes.date),
  status: CouponStatus.ACTIVE
}

const CouponModal = ({ onHide, ...rest }) => {
  const [error, setError] = useState('')

  const onSubmit = useCallback(
    async (values, { setSubmitting }) => {
      try {
        setSubmitting(true)
        const {
          id,
          code,
          percentage,
          quantity,
          expiration,
          status,
          ...restValues
        } = values

        const coupon = trimAllStrProps({
          ...restValues,
          ...(id && { id }),
          code: code.toUpperCase(),
          percentage: Number(percentage.replace(',', '.')),
          quantity: Number(quantity),
          expiration: dayjs(expiration).format(dateTypes.endDate),
          status: status.value
        })

        await ecbrApi.coupons.create(coupon)
        onHide?.(false)
        setError('')
      } catch (err) {
        errorResponseStatusHandler(err, setError)
      } finally {
        setSubmitting(false)
      }
    },
    [onHide, setError]
  )

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        id: Yup.number().optional(),
        code: Yup.string()
          .required('Campo obrigatório')
          .test(
            'coupon-exists',
            'Cupom já existe',
            async (value) => (await isAvailableCoupon(value, setError)) ?? true
          ),
        name: Yup.string().required('Campo obrigatório'),
        percentage: Yup.number()
          .required('Campo obrigatório')
          .typeError('Esse campo deve conter somente números')
          .transform((_, value) => Number(value.replace(',', '.')))
          .test(
            'is-number',
            'Esse campo deve conter somente números',
            (value) => !isNaN(value)
          )
          .min(1, 'O valor mínimo é 1')
          .max(100, 'O valor máximo é 100'),
        quantity: Yup.number()
          .required('Campo obrigatório')
          .min(0, 'Valor inválido'),
        expiration: Yup.date()
          .required('Campo obrigatório')
          .min(
            dayjs().format(dateTypes.date),
            'Data deve ser maior ou igual do que a atual'
          ),
        status: Yup.string()
          .required('Campo obrigatório')
          .transform((_, obj) => obj?.value ?? undefined)
      }),
    []
  )

  return (
    <Modal onHide={onHide} exitOnBackdropClick={false} {...rest}>
      <Modal.Header closeButton>
        <Modal.Title>Novo Cupom</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={formSchema}
        enableReinitialize
        validateOnChange={false}
      >
        {({ handleSubmit }) => (
          <>
            <Modal.Body>
              <Form>
                <FormBody>
                  <Typography type="text" spacing="0 0 1rem 0" align="center">
                    Dados do Cupom
                  </Typography>
                  <FormGroupContainer>
                    <Input
                      type="text"
                      name="code"
                      label="Identificação"
                      placeholder="Código do cupom"
                      textTransform="uppercase"
                    />
                    <Select
                      name="status"
                      label="Status"
                      placeholder="Selecione o status"
                      options={[
                        { value: CouponStatus.ACTIVE, label: 'Ativo' },
                        { value: CouponStatus.INACTIVE, label: 'Inativo' }
                      ]}
                    />
                  </FormGroupContainer>
                  <Input
                    type="text"
                    name="name"
                    label="Nome"
                    placeholder="Nome do cupom"
                  />
                  <FormGroupContainer>
                    <Input
                      type="text"
                      name="percentage"
                      label="Percentual"
                      placeholder="Percentual de desconto"
                      maxlength="5"
                    />
                    <Input
                      type="text"
                      name="quantity"
                      label="Quantidade"
                      placeholder="Quantidade de cupons"
                      noComma={true}
                    />
                  </FormGroupContainer>
                  <Input
                    type="date"
                    name="expiration"
                    label="Validade"
                    placeholder="Data de validade"
                  />
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                </FormBody>
              </Form>
            </Modal.Body>
            <Modal.Footer align="center">
              <Button
                text="Cancelar"
                variant="outlined"
                width="200px"
                onClick={() => {
                  onHide?.(false)
                  setError('')
                }}
              />
              <Button
                text="Salvar"
                variant="primary"
                width="200px"
                onClick={handleSubmit}
              />
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default CouponModal
